import { normalizeEffectBehaviorSummary } from "@granular-software/metamodel-effect-behaviors";
import {
  createFeedPublisher,
  type FeedPublishTransport,
  type FeedPublisher,
  type TransientFeedbackHandle,
} from "./feed";
import {
  canonicalizeCommitValue,
  type CommitTransport,
  validateProjectionResult,
} from "./commit";
import type {
  CommitReceipt,
  EffectCommitContext,
  EffectCommitRequest,
  EffectVersionSelector,
  EffectHandlerContext,
  EffectInvocationMode,
  ManifestEffectMetamodelSpec,
  ProjectionResult,
  ResolvedEffectBehaviors,
  ToolWithHandler,
} from "./types";

type EffectRuntimeWireContext = Omit<EffectHandlerContext, "commit"> & {
  commit?: never;
};

type EffectRuntimeRequest = {
  effectKey: string;
  effectName: string;
  input: unknown;
  context?: EffectRuntimeWireContext;
};

export interface EffectRuntimeFeedbackBridge {
  /** Ephemeral registry capability for this one active invocation. */
  invocationId: string;
  publish: FeedPublishTransport;
}

export interface InvokeRegisteredEffectOptions {
  feedback?: EffectRuntimeFeedbackBridge;
  /** Durable product-acknowledgement transport for declared write effects. */
  commit?: CommitTransport;
  /** Internal effect-host hook used to return the durable receipt separately from the product result. */
  commitAcknowledged?: (receipt: CommitReceipt) => void | Promise<void>;
}

type ResolvedHandler = {
  effect: ToolWithHandler;
  mode: EffectInvocationMode;
  handler: ToolWithHandler["handler"];
};

function computeEffectKey(effect: {
  name: string;
  className?: string | null;
  static?: boolean;
}): string {
  const attachedClass = effect.className?.trim();
  if (!attachedClass) {
    return `global:${effect.name}`;
  }
  return effect.static
    ? `class:${attachedClass}:static:${effect.name}`
    : `class:${attachedClass}:instance:${effect.name}`;
}

function computeEffectVersionSelectorSpecificity(
  selector?: EffectVersionSelector,
): number {
  if (!selector || selector.mode === "all") {
    return 0;
  }
  if (selector.mode === "exact") {
    return 2;
  }
  return 1;
}

function matchesEffectVersionSelector(
  selector: EffectVersionSelector | undefined,
  buildVersionNumber: number | undefined,
): boolean {
  if (!selector || selector.mode === "all") {
    return true;
  }
  if (
    typeof buildVersionNumber !== "number" ||
    !Number.isFinite(buildVersionNumber)
  ) {
    return false;
  }
  if (selector.mode === "exact") {
    return buildVersionNumber === selector.versionNumber;
  }
  if (selector.mode === "before") {
    return buildVersionNumber < selector.versionNumber;
  }
  return buildVersionNumber > selector.versionNumber;
}

function selectRegisteredEffect(
  effectMap: Map<string, ToolWithHandler>,
  effectKey: string,
  buildVersionNumber?: number,
): ToolWithHandler | undefined {
  let bestEffect: ToolWithHandler | undefined;
  let bestSpecificity = Number.NEGATIVE_INFINITY;

  for (const effect of effectMap.values()) {
    if (computeEffectKey(effect) !== effectKey) {
      continue;
    }
    if (
      !matchesEffectVersionSelector(effect.versionSelector, buildVersionNumber)
    ) {
      continue;
    }

    const specificity = computeEffectVersionSelectorSpecificity(
      effect.versionSelector,
    );
    if (!bestEffect || specificity > bestSpecificity) {
      bestEffect = effect;
      bestSpecificity = specificity;
    }
  }

  return bestEffect;
}

export function normalizeEffectBehaviors(
  value?: ManifestEffectMetamodelSpec | ResolvedEffectBehaviors | null,
): ResolvedEffectBehaviors {
  return (normalizeEffectBehaviorSummary(
    value as Record<string, unknown> | null | undefined,
  ) || {}) as ResolvedEffectBehaviors;
}

function resolveInvocationMode(
  context?: EffectRuntimeWireContext,
): EffectInvocationMode {
  const mode = context?.invocation?.mode;
  if (mode === "dryRun" || mode === "reverse" || mode === "artifactOptions") {
    return mode;
  }
  return "execute";
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

async function resolveInvocationIdempotencyKey(
  request: EffectRuntimeRequest,
): Promise<string> {
  const supplied = request.context?.idempotencyKey?.trim();
  if (supplied) return supplied;
  const invocationId = request.context?.invocationId?.trim();
  if (!invocationId) {
    throw new Error(
      `Committed effect ${request.effectKey} requires an invocation id`,
    );
  }
  const digest = await sha256Hex(
    canonicalizeCommitValue({
      sandboxId: request.context?.sandboxId || "",
      environmentId: request.context?.environmentId || "",
      effectKey: request.effectKey,
      invocationId,
      input: request.input,
    }),
  );
  return `gci_${digest}`;
}

function createUnavailableCommitContext(message: string): EffectCommitContext {
  const unavailable = async (): Promise<never> => {
    throw new Error(message);
  };
  return {
    effect: unavailable,
    transition: unavailable,
  };
}

function resolveReverseEffect(
  effectMap: Map<string, ToolWithHandler>,
  currentEffect: ToolWithHandler,
  request: EffectRuntimeRequest,
  behaviors: ResolvedEffectBehaviors,
): ToolWithHandler | undefined {
  const explicitReverseHandler =
    request.context?.invocation?.reverseHandler?.trim();
  const configuredReverseHandler = behaviors.reverse?.handler?.trim();
  const reverseHandler = explicitReverseHandler || configuredReverseHandler;
  if (!reverseHandler) {
    return undefined;
  }

  if (reverseHandler.includes(":")) {
    return selectRegisteredEffect(
      effectMap,
      reverseHandler,
      request.context?.buildVersionNumber,
    );
  }

  const directMatch = selectRegisteredEffect(
    effectMap,
    reverseHandler,
    request.context?.buildVersionNumber,
  );
  if (directMatch) {
    return directMatch;
  }

  const candidateKeys = [
    computeEffectKey({
      name: reverseHandler,
      className: currentEffect.className,
      static: currentEffect.static,
    }),
    computeEffectKey({
      name: reverseHandler,
    }),
  ];

  for (const candidateKey of candidateKeys) {
    const candidate = selectRegisteredEffect(
      effectMap,
      candidateKey,
      request.context?.buildVersionNumber,
    );
    if (candidate) {
      return candidate;
    }
  }

  return undefined;
}

function resolveHandlerForMode(
  effectMap: Map<string, ToolWithHandler>,
  effect: ToolWithHandler,
  request: EffectRuntimeRequest,
): ResolvedHandler {
  const behaviors = normalizeEffectBehaviors(
    request.context?.behaviors || effect.metamodels || undefined,
  );
  const mode = resolveInvocationMode(request.context);

  if (mode === "artifactOptions") {
    if (!effect.artifactOptionsHandler) {
      throw new Error(
        `Artifact field options are not supported for ${request.effectKey}`,
      );
    }
    return {
      effect,
      mode,
      handler: effect.artifactOptionsHandler,
    };
  }

  if (mode === "dryRun") {
    if (effect.dryRunHandler) {
      return { effect, mode, handler: effect.dryRunHandler };
    }
    if (behaviors.dryRun?.enabled) {
      return { effect, mode, handler: effect.handler };
    }
    throw new Error(`Dry run is not supported for ${request.effectKey}`);
  }

  if (mode === "reverse") {
    // A named reverse effect is resolved and reserved by the orchestrator
    // before it reaches the provider. In that case this effect *is* the
    // reversal operation, so its normal handler and commit declaration must
    // remain bound to the effect key carried by the request.
    const sourceEffectKey =
      request.context?.invocation?.sourceEffectKey?.trim();
    if (sourceEffectKey && sourceEffectKey !== request.effectKey) {
      return { effect, mode, handler: effect.handler };
    }
    const namedReverseHandler =
      request.context?.invocation?.reverseHandler?.trim() ||
      behaviors.reverse?.handler?.trim();
    if (namedReverseHandler) {
      const reverseEffect = resolveReverseEffect(
        effectMap,
        effect,
        request,
        behaviors,
      );
      if (reverseEffect) {
        return {
          effect: reverseEffect,
          mode,
          handler: reverseEffect.handler,
        };
      }
      throw new Error(
        `Reverse effect ${namedReverseHandler} is not registered for ${request.effectKey}`,
      );
    }
    if (effect.reverseHandler) {
      return { effect, mode, handler: effect.reverseHandler };
    }

    throw new Error(
      `Reverse execution is not supported for ${request.effectKey}`,
    );
  }

  return { effect, mode, handler: effect.handler };
}

function createInvocationFeedbackContext(bridge: EffectRuntimeFeedbackBridge): {
  feedback: FeedPublisher["feedback"];
  transientFeedback: FeedPublisher["transientFeedback"];
  flush: () => Promise<void>;
} {
  const publications: Promise<unknown>[] = [];
  let nextOperationOrdinal = 0;
  const nextOperationId = (kind: string): string =>
    `${kind}:${nextOperationOrdinal++}`;
  const track = <T>(publication: Promise<T>): Promise<T> => {
    const tracked = Promise.resolve(publication);
    publications.push(tracked);
    // Flush observes every tracked rejection. Attach a handler immediately so
    // an intentionally unawaited publication cannot trigger a process-level
    // unhandled-rejection warning before the effect handler returns.
    void tracked.catch(() => undefined);
    return tracked;
  };
  const publisher = createFeedPublisher((method, params) =>
    bridge.publish(method, {
      ...params,
      invocationId: bridge.invocationId,
    }),
  );

  const wrapTransientHandle = (
    initial: TransientFeedbackHandle,
  ): TransientFeedbackHandle => {
    let current = initial;
    const wrapped: TransientFeedbackHandle = {
      get id() {
        return current.id;
      },
      get ordinal() {
        return current.ordinal;
      },
      get revision() {
        return current.revision;
      },
      async update(text, options = {}) {
        current = await track(
          current.update(text, {
            ...options,
            operationId:
              options.operationId || nextOperationId("transient-update"),
          }),
        );
        return wrapped;
      },
      settle(text, options = {}) {
        return track(
          current.settle(text, {
            ...options,
            operationId:
              options.operationId || nextOperationId("transient-settle"),
          }),
        );
      },
    };
    return wrapped;
  };

  return {
    feedback(text, options = {}) {
      return track(
        publisher.feedback(text, {
          ...options,
          operationId: options.operationId || nextOperationId("feedback"),
        }),
      );
    },
    transientFeedback(text, options = {}) {
      return track(
        publisher
          .transientFeedback(text, {
            ...options,
            operationId:
              options.operationId || nextOperationId("transient-create"),
          })
          .then(wrapTransientHandle),
      );
    },
    async flush() {
      let cursor = 0;
      let firstError: unknown;
      // A settled create can schedule an update while an earlier batch is
      // being awaited, so continue until no new publication was registered.
      while (cursor < publications.length) {
        const batch = publications.slice(cursor);
        cursor = publications.length;
        const results = await Promise.allSettled(batch);
        for (const result of results) {
          if (result.status === "rejected" && firstError === undefined) {
            firstError = result.reason;
          }
        }
      }
      if (firstError !== undefined) {
        throw firstError;
      }
    },
  };
}

export async function invokeRegisteredEffect(
  effectMap: Map<string, ToolWithHandler>,
  request: EffectRuntimeRequest,
  options: InvokeRegisteredEffectOptions = {},
): Promise<unknown> {
  const effect = selectRegisteredEffect(
    effectMap,
    request.effectKey,
    request.context?.buildVersionNumber,
  );
  if (!effect) {
    throw new Error(`Effect handler not found: ${request.effectKey}`);
  }

  const resolved = resolveHandlerForMode(effectMap, effect, request);
  const isPreResolvedNamedReverse =
    resolved.mode === "reverse" &&
    Boolean(request.context?.invocation?.sourceEffectKey?.trim()) &&
    request.context?.invocation?.sourceEffectKey?.trim() !== request.effectKey;
  const resolvedEffectKey = computeEffectKey(resolved.effect);
  if (
    resolved.mode === "reverse" &&
    resolved.effect !== effect &&
    (effect.commit || resolved.effect.commit)
  ) {
    throw new Error(
      `Committed named reverse effect ${resolvedEffectKey} must be reserved and dispatched using its own effect key; customer code was not invoked`,
    );
  }
  const declaration = resolved.effect.commit;
  const commitTransport = options.commit;
  const commitRequired =
    Boolean(declaration) &&
    (resolved.mode === "execute" || resolved.mode === "reverse");
  if (commitRequired && !commitTransport) {
    throw new Error(
      `Committed effect ${request.effectKey} has no durable commit transport; customer code was not invoked`,
    );
  }
  if (
    commitRequired &&
    declaration?.kind === "transition" &&
    !request.context?.invocation?.transition
  ) {
    throw new Error(
      `Transition effect ${request.effectKey} has no resolved transition context; customer code was not invoked`,
    );
  }

  const idempotencyKey = commitRequired
    ? await resolveInvocationIdempotencyKey(request)
    : request.context?.idempotencyKey;
  let commitStarted = false;
  let commitPromise: Promise<CommitReceipt> | null = null;

  const beginCommit = (
    requestedKind: "effect" | "transition",
    productResult: unknown,
  ): Promise<CommitReceipt> => {
    if (!declaration || !commitRequired || !commitTransport) {
      return Promise.reject(
        new Error(
          `Effect ${request.effectKey} does not declare an active ${requestedKind} commit`,
        ),
      );
    }
    if (declaration.kind !== requestedKind) {
      return Promise.reject(
        new Error(
          `Effect ${request.effectKey} declares ${declaration.kind} commit, not ${requestedKind}`,
        ),
      );
    }
    if (commitStarted) {
      return Promise.reject(
        new Error(
          `Effect invocation ${request.context?.invocationId || request.effectKey} already emitted its commit`,
        ),
      );
    }
    commitStarted = true;

    commitPromise = (async () => {
      let projection: ProjectionResult;
      try {
        projection = (
          declaration.project as (result: unknown) => ProjectionResult
        )(productResult);
        validateProjectionResult(declaration, projection);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (commitTransport.mappingFailed) {
          await commitTransport.mappingFailed({
            effectKey: request.effectKey,
            effectName: request.effectName,
            invocationId: request.context?.invocationId || "",
            idempotencyKey: idempotencyKey || "",
            environmentId: request.context?.environmentId || "",
            message,
          });
        }
        throw new Error(
          `Product mutation may have succeeded, but its commit projection is invalid: ${message}`,
        );
      }

      if (requestedKind === "transition") {
        const transition = request.context?.invocation?.transition;
        const outcome = (projection as { outcome?: { key?: string } }).outcome;
        if (!transition || !outcome?.key) {
          throw new Error(
            `Transition effect ${request.effectKey} did not resolve an authored outcome`,
          );
        }
        if (
          !Object.prototype.hasOwnProperty.call(
            transition.outcomes,
            outcome.key,
          )
        ) {
          throw new Error(
            `Transition ${transition.machine}.${transition.transition} does not declare outcome ${outcome.key}`,
          );
        }
      }

      const invocationId = request.context?.invocationId || "";
      const environmentId = request.context?.environmentId || "";
      const sandboxId = request.context?.sandboxId || "";
      if (!invocationId || !environmentId || !sandboxId || !idempotencyKey) {
        throw new Error(
          `Committed effect ${request.effectKey} is missing its trusted invocation scope`,
        );
      }

      const commitRequest: EffectCommitRequest = {
        kind: requestedKind,
        effectKey: request.effectKey,
        effectName: request.effectName,
        operationLabel: resolved.effect.label || resolved.effect.name,
        invocationId,
        idempotencyKey,
        sandboxId,
        environmentId,
        ...(request.context?.sessionId
          ? { sessionId: request.context.sessionId }
          : {}),
        ...(request.context?.jobId ? { jobId: request.context.jobId } : {}),
        ...(request.context?.buildId
          ? { buildId: request.context.buildId }
          : {}),
        ...(request.context?.invocation?.artifactId
          ? { artifactId: request.context.invocation.artifactId }
          : {}),
        projection,
        ...(requestedKind === "transition" &&
        request.context?.invocation?.transition
          ? { transition: request.context.invocation.transition }
          : {}),
      };
      const receipt = await commitTransport.persist(commitRequest);
      await options.commitAcknowledged?.(receipt);
      return receipt;
    })();
    return commitPromise;
  };

  const commitContext: EffectCommitContext = commitRequired
    ? {
        effect: (productResult) => beginCommit("effect", productResult),
        transition: (productResult) => beginCommit("transition", productResult),
      }
    : createUnavailableCommitContext(
        `Effect ${request.effectKey} is not executing a declared product commit`,
      );
  const context: EffectHandlerContext = {
    ...(request.context || {}),
    ...(idempotencyKey ? { idempotencyKey } : {}),
    commit: commitContext,
    behaviors: normalizeEffectBehaviors(
      request.context?.behaviors || effect.metamodels || undefined,
    ),
    invocation: {
      mode: resolved.mode,
      ...(commitRequired && declaration
        ? { commitKind: declaration.kind }
        : {}),
      sourceEffectKey: isPreResolvedNamedReverse
        ? request.context?.invocation?.sourceEffectKey || request.effectKey
        : request.effectKey,
      sourceEffectName: isPreResolvedNamedReverse
        ? request.context?.invocation?.sourceEffectName || request.effectName
        : request.effectName,
      ...(request.context?.invocation?.reverseHandler
        ? { reverseHandler: request.context.invocation.reverseHandler }
        : {}),
      ...(request.context?.invocation?.artifactId
        ? { artifactId: request.context.invocation.artifactId }
        : {}),
      ...(request.context?.invocation?.artifactOptions
        ? { artifactOptions: request.context.invocation.artifactOptions }
        : {}),
      ...(request.context?.invocation?.transition
        ? { transition: request.context.invocation.transition }
        : {}),
    },
  } as EffectHandlerContext;
  const feedbackContext = options.feedback
    ? createInvocationFeedbackContext(options.feedback)
    : null;
  if (feedbackContext) {
    context.feedback = feedbackContext.feedback;
    context.transientFeedback = feedbackContext.transientFeedback;
  }

  let handlerResult: unknown;
  let handlerError: unknown;
  let handlerFailed = false;
  try {
    if (resolved.mode === "artifactOptions") {
      handlerResult = await (resolved.handler as any)(request.input, context);
    } else if (
      resolved.effect.className &&
      !resolved.effect.static &&
      request.input &&
      typeof request.input === "object" &&
      "_objectId" in (request.input as Record<string, unknown>)
    ) {
      const { _objectId, ...rest } = request.input as Record<
        string,
        unknown
      > & {
        _objectId: string;
      };
      handlerResult = await (resolved.handler as any)(_objectId, rest, context);
    } else {
      handlerResult = await (resolved.handler as any)(request.input, context);
    }
  } catch (error) {
    handlerFailed = true;
    handlerError = error;
  }

  let commitError: unknown;
  let commitFailed = false;
  if (commitPromise) {
    try {
      await commitPromise;
    } catch (error) {
      commitFailed = true;
      commitError = error;
    }
  }

  let feedbackError: unknown;
  let feedbackFailed = false;
  if (feedbackContext) {
    try {
      await feedbackContext.flush();
    } catch (error) {
      feedbackFailed = true;
      feedbackError = error;
    }
  }
  if (handlerFailed) {
    throw handlerError;
  }
  if (commitFailed) {
    throw commitError;
  }
  if (feedbackFailed) {
    throw feedbackError;
  }
  if (commitRequired && !commitStarted) {
    throw new Error(
      `Mutating effect ${request.effectKey} returned without acknowledging its product mutation`,
    );
  }
  return handlerResult;
}
